// ═══════════════════════════════════════════════════════════════
// MainStreet — Organization Lists Manager
// CRUD for named lists of organizations
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Textarea,
  ActionIcon, Modal, Stack, MultiSelect, Collapse,
} from '../../components/gab/GabComponents';
import {
  IconPlus, IconPencil, IconTrash, IconRefresh, IconChevronDown, IconChevronRight,
} from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { useMainStreetStore } from '../../store/mainstreetStore';
import type { MSOrgList } from '../../store/mainstreetStore';
import { useModulePermission } from '../../hooks/useModulePermission';

const EMPTY: Partial<MSOrgList> = {
  name: '', desc: '', orgIds: '',
};

export default function Lists() {
  const { canEdit } = useModulePermission('mainstreet');
  const {
    organizations, orgLists, loadingLists,
    loadOrganizations, loadOrgLists,
    createOrgList, updateOrgList, deleteOrgList,
  } = useMainStreetStore();

  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<MSOrgList | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<MSOrgList | null>(null);
  const [form, setForm] = useState<Partial<MSOrgList>>({ ...EMPTY });
  const [formOrgIds, setFormOrgIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});

  useEffect(() => {
    loadOrganizations();
    loadOrgLists();
  }, []);

  const orgMultiSelect = useMemo(() =>
    organizations.map(o => ({ value: String(o.id), label: o.name || `Org #${o.id}` })),
  [organizations]);

  const orgName = (id: string) => organizations.find(o => String(o.id) === id)?.name ?? `#${id}`;

  const parseOrgIds = (csv: string): string[] =>
    csv.split(',').map(s => s.trim()).filter(Boolean);

  const toggleExpand = (id: number) =>
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  const openCreate = () => {
    setEditTarget(null);
    setForm({ ...EMPTY });
    setFormOrgIds([]);
    setModalOpen(true);
  };

  const openEdit = (list: MSOrgList) => {
    setEditTarget(list);
    setForm({ ...list });
    setFormOrgIds(parseOrgIds(list.orgIds));
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.name?.trim()) {
      notifications.show({ message: 'List name is required', color: 'yellow' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        orgIds: formOrgIds.join(','),
      };
      if (editTarget) {
        await updateOrgList(editTarget.id, payload);
        notifications.show({ message: 'List updated', color: 'green' });
      } else {
        await createOrgList({
          ...payload,
          createdAt: new Date().toISOString(),
        });
        notifications.show({ message: 'List created', color: 'green' });
      }
      setModalOpen(false);
      setEditTarget(null);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Save failed', color: 'red' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await deleteOrgList(deleteTarget.id);
      notifications.show({ message: `${deleteTarget.name} deleted`, color: 'gray' });
      setDeleteTarget(null);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Delete failed', color: 'red' });
    } finally {
      setSaving(false);
    }
  };

  const sorted = useMemo(() =>
    [...orgLists].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
  [orgLists]);

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Organization Lists</Title>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />}
            loading={loadingLists} onClick={() => loadOrgLists()}>Refresh</Button>
          {canEdit && <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>Add List</Button>}
        </Group>
      </Group>

      <Text size="xs" c="dimmed" mb="xs">{sorted.length} list{sorted.length !== 1 ? 's' : ''}</Text>

      <Paper withBorder>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid var(--mantine-color-gray-3)' }}>
              <th style={{ padding: '8px 12px', textAlign: 'left', width: 32 }}></th>
              <th style={{ padding: '8px 12px', textAlign: 'left' }}>Name</th>
              <th style={{ padding: '8px 12px', textAlign: 'left' }}>Description</th>
              <th style={{ padding: '8px 12px', textAlign: 'center' }}>Orgs</th>
              <th style={{ padding: '8px 12px', textAlign: 'left' }}>Created</th>
              <th style={{ padding: '8px 12px', textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 && (
              <tr>
                <td colSpan={6} style={{ padding: 24, textAlign: 'center', color: '#888' }}>
                  {loadingLists ? 'Loading...' : 'No lists yet'}
                </td>
              </tr>
            )}
            {sorted.map(list => {
              const ids = parseOrgIds(list.orgIds);
              const isExpanded = !!expanded[list.id];
              return (
                <tr key={list.id} style={{ borderBottom: '1px solid var(--mantine-color-gray-2)', verticalAlign: 'top' }}>
                  <td style={{ padding: '8px 4px 8px 12px' }}>
                    {ids.length > 0 && (
                      <ActionIcon size="xs" variant="subtle" onClick={() => toggleExpand(list.id)}>
                        {isExpanded ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
                      </ActionIcon>
                    )}
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <div>
                      <Text size="sm" fw={500} style={{ cursor: ids.length > 0 ? 'pointer' : 'default' }}
                        onClick={() => ids.length > 0 && toggleExpand(list.id)}>
                        {list.name || '(unnamed)'}
                      </Text>
                      <Collapse in={isExpanded}>
                        <div style={{ marginTop: 8 }}>
                          {ids.map(id => (
                            <Badge key={id} size="xs" variant="light" mr={4} mb={4}>{orgName(id)}</Badge>
                          ))}
                        </div>
                      </Collapse>
                    </div>
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <Text size="sm" c="dimmed" lineClamp={2}>{list.desc || '-'}</Text>
                  </td>
                  <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                    <Badge size="xs" variant="light">{ids.length}</Badge>
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    {list.createdAt ? new Date(list.createdAt).toLocaleDateString() : '-'}
                  </td>
                  <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                    {canEdit && (
                      <Group gap={4} justify="flex-end">
                        <ActionIcon size="sm" variant="subtle" onClick={() => openEdit(list)}>
                          <IconPencil size={14} />
                        </ActionIcon>
                        <ActionIcon size="sm" variant="subtle" color="red" onClick={() => setDeleteTarget(list)}>
                          <IconTrash size={14} />
                        </ActionIcon>
                      </Group>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Paper>

      {/* Create / Edit Modal */}
      <Modal opened={modalOpen} onClose={() => setModalOpen(false)}
        title={editTarget ? 'Edit List' : 'Add List'} centered size="md">
        <Stack>
          <TextInput label="Name" required value={form.name ?? ''}
            onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))} />
          <Textarea label="Description" value={form.desc ?? ''}
            onChange={e => setForm(prev => ({ ...prev, desc: e.target.value }))} autosize minRows={2} />
          <MultiSelect label="Organizations" data={orgMultiSelect} value={formOrgIds}
            onChange={setFormOrgIds} searchable placeholder="Select organizations..." />

          <Group justify="flex-end">
            <Button variant="default" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button loading={saving} onClick={handleSave}>
              {editTarget ? 'Save Changes' : 'Create List'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Delete confirmation */}
      <Modal opened={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete List" centered size="sm">
        <Stack>
          <Text size="sm">Are you sure you want to delete <strong>{deleteTarget?.name}</strong>?</Text>
          <Group justify="flex-end">
            <Button variant="default" size="xs" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button color="red" size="xs" loading={saving} onClick={handleDelete}>Delete</Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
}
